import { ImageIcon, Upload } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ACCENT_PRESETS } from "@/features/settings/components/tabs/branding-section";
import { FIELD_LABEL } from "@/features/settings/components/settings-section";
import { type FirmSettings, getFirmSettings, uploadFirmLogo } from "@/features/settings/firm-api";
import { useAuthedImage } from "@/hooks/use-authed-image";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { HubStepShell } from "../hub/hub-step-shell";
import type { OnboardingStepProps } from "./types";

// Mirror the backend's logo validation (FirmSettingsSerializer caps images at 2MB).
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

/** Step 2 — logo upload + accent colour. Logo persists via `uploadFirmLogo`;
 * the accent colour is UI-only for now (no `accent_color` backend field yet). */
export function BrandingStep({
  meta,
  index,
  isFirst,
  onBack,
  onSkip,
  onSaved,
}: OnboardingStepProps) {
  const [settings, setSettings] = useState<FirmSettings | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [accent, setAccent] = useState(ACCENT_PRESETS[0]!.hex);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getFirmSettings()
      .then(setSettings)
      .catch(() => toast.error("Couldn't load your branding."));
  }, []);

  // Preview the freshly-picked file locally; fall back to the stored logo.
  const storedLogo = useAuthedImage(
    settings?.logoUrl ? `/firm/settings/media/logo?v=${encodeURIComponent(settings.logoUrl)}` : null,
  );
  const shownLogo = localPreview ?? storedLogo;

  function pick(f: File | null) {
    if (f && f.size > MAX_LOGO_BYTES) {
      toast.error("That image is over 2MB — pick a smaller one.");
      return;
    }
    setFile(f);
    setLocalPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return f ? URL.createObjectURL(f) : null;
    });
  }

  useEffect(() => () => {
    if (localPreview) URL.revokeObjectURL(localPreview);
  }, [localPreview]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (file) await uploadFirmLogo(file);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't upload your logo.");
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={save}>
      <HubStepShell
        meta={meta}
        index={index}
        onBack={onBack}
        onSkip={onSkip}
        isFirst={isFirst}
        submitting={submitting}
      >
        {/* Logo */}
        <div className="flex flex-col gap-2">
          <span className={FIELD_LABEL}>Logo</span>
          <div className="flex flex-wrap items-center gap-3.5">
            <div className="bg-card flex h-[88px] w-28 shrink-0 items-center justify-center overflow-hidden rounded-md border">
              {shownLogo ? (
                <img src={shownLogo} alt="Firm logo" className="max-h-full object-contain" />
              ) : (
                <span className="text-muted-foreground flex flex-col items-center gap-1 text-[11px] font-semibold tracking-wide uppercase">
                  <ImageIcon className="text-info size-6" strokeWidth={1.8} />
                  [Your logo]
                </span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/svg+xml,image/jpeg"
                hidden
                onChange={(e) => pick(e.target.files?.[0] ?? null)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-dashed"
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="text-info" />
                {shownLogo ? "Replace logo" : "Upload logo"}
              </Button>
              <span className="text-muted-foreground text-xs">
                PNG, JPG or SVG, up to 2MB.
              </span>
            </div>
          </div>
        </div>

        {/* Accent colour */}
        <div className="flex flex-col gap-2">
          <Label className={FIELD_LABEL}>Accent colour</Label>
          <div role="group" aria-label="Accent colour" className="flex flex-wrap items-center gap-2">
            {ACCENT_PRESETS.map((preset) => {
              const selected = accent.toUpperCase() === preset.hex.toUpperCase();
              return (
                <button
                  key={preset.hex}
                  type="button"
                  aria-label={preset.name}
                  aria-pressed={selected}
                  onClick={() => setAccent(preset.hex)}
                  className={cn(
                    "size-8 rounded-full border-[3px] border-white transition-transform hover:scale-110",
                    selected ? "ring-2 ring-offset-0" : "ring-border ring-1",
                  )}
                  style={{
                    backgroundColor: preset.hex,
                    ...(selected ? { boxShadow: `0 0 0 2px ${preset.hex}` } : {}),
                  }}
                />
              );
            })}
          </div>
          <span className="text-muted-foreground text-xs">
            Used for headings and rules on letters, and buttons in the client portal.
          </span>
        </div>
      </HubStepShell>
    </form>
  );
}
